import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Abonnement, SESSION_PORTAIL } from '@semply/theme/Abonnement';
import { API_URL } from '../../lib/api';
import { dossiers, facturation, NOMS_PRODUITS, PRODUITS, type Facturation, type Produit, type ProduitPropose } from '../../lib/dossiers';
import { BlocFacturation } from '../dossiers/BlocFacturation';
import { apiEspace } from '../../lib/espace';
import { FacturesEspace } from './FacturesEspace';
import { PaiementStripe } from '../../components/PaiementStripe';
import { AuthCard } from '../../components/AuthCard';
import { useSessionPortail } from '../dossiers/useSessionPortail';
import '../../styles/dossiers.css';

/** Ce que chaque produit compte et dit de lui (repris des écrans des produits). */
const PRESENTATION: Record<
  Produit,
  { description: string; unite?: { singulier: string; pluriel: string }; avecQuantite?: boolean }
> = {
  APP: {
    description: 'Facturation, achats, banque, déclarations : la formule fixe ce qui est ouvert dans votre dossier.',
    avecQuantite: false,
  },
  COMPTA: {
    description:
      'Saisie, lettrage, TVA, FEC, bilan. La quantité est le nombre de dossiers comptables que vous pouvez tenir, sur l’ensemble de votre espace.',
    unite: { singulier: 'dossier comptable', pluriel: 'dossiers comptables' },
  },
  IMMO: {
    description: 'Gestion de votre patrimoine immobilier. La quantité multiplie le nombre de dossiers inclus dans la formule.',
    unite: { singulier: 'lot de dossiers', pluriel: 'lots de dossiers' },
  },
  PREVI: {
    description:
      'Prévisionnels et estimations. La quantité est le nombre de prévisionnels que vous pouvez tenir à jour, sur l’ensemble des dossiers de votre espace.',
    unite: { singulier: 'prévisionnel', pluriel: 'prévisionnels' },
  },
};

/** Retour vers un produit : seulement une origine de la suite (même règle que la création). */
function retourSur(brut: string | null, produits: ProduitPropose[]): string | null {
  if (!brut) return null;
  try {
    const url = new URL(brut);
    const origines = produits.map((p) => (p.url ? new URL(p.url).origin : null)).filter(Boolean);
    return origines.includes(url.origin) ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * « Mes abonnements » du portail (11/10/2026).
 *
 * Tout le paiement — formules, offres groupées, dossiers supplémentaires,
 * packs, codes — se fait ici pour toute la suite : les écrans « Mon
 * abonnement » des produits y renvoient (`?produit=compta&retour=…`). Écran
 * commun @semply/theme/Abonnement, appelé avec la session du portail.
 */
export function AbonnementsPage() {
  const { pret } = useSessionPortail();
  const [params, setParams] = useSearchParams();
  const [produits, setProduits] = useState<ProduitPropose[]>([]);
  const [compte, setCompte] = useState<{ spaceId: string; gestionnaire: boolean; facturation: Facturation } | null>(null);
  // Plusieurs espaces = plusieurs payeurs (29/10/2026) : on choisit lequel on gère.
  const [espaces, setEspaces] = useState<{ id: string; name: string }[]>([]);
  const espaceDemande = params.get('espace');
  const espaceId = espaces.some((e) => e.id === espaceDemande) ? espaceDemande : (espaces[0]?.id ?? null);

  const chargerCompte = () =>
    facturation
      .compte(espaceId)
      .then((c) =>
        setCompte(
          c.space
            ? { spaceId: c.space.id, gestionnaire: c.role === 'OWNER' || c.role === 'ADMIN', facturation: c.facturation }
            : null,
        ),
      )
      .catch(() => setCompte(null));

  useEffect(() => {
    if (!pret) return;
    dossiers.produits().then(setProduits).catch(() => setProduits([]));
    apiEspace
      .apercu()
      .then((a) => setEspaces((a.espaces ?? []).map((e) => ({ id: e.id, name: e.name }))))
      .catch(() => setEspaces([]));
  }, [pret]);

  useEffect(() => {
    if (!pret) return;
    void chargerCompte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret, espaceId]);

  const choisirEspace = (id: string) => {
    const suite = new URLSearchParams(params);
    suite.set('espace', id);
    setParams(suite, { replace: true });
  };

  const lances = useMemo(() => {
    const ouverts = produits.filter((p) => p.lance).map((p) => p.produit);
    return ouverts.length > 0 ? ouverts : PRODUITS;
  }, [produits]);

  const demande = (params.get('produit') ?? '').toUpperCase() as Produit;
  const produit: Produit = lances.includes(demande) ? demande : lances[0];
  const retour = retourSur(params.get('retour'), produits);

  if (!pret) return <AuthCard title="Chargement…">{null}</AuthCard>;

  const choisir = (p: Produit) => {
    const suite = new URLSearchParams(params);
    suite.set('produit', p.toLowerCase());
    setParams(suite, { replace: true });
  };

  const presentation = PRESENTATION[produit];
  const nomProduit = produits.find((p) => p.produit === produit)?.nom ?? NOMS_PRODUITS[produit];
  const retourStripe =
    `${window.location.origin}/abonnements/retour?produit=${produit.toLowerCase()}` +
    `${espaceId ? `&espace=${encodeURIComponent(espaceId)}` : ''}` +
    `${retour ? `&retour=${encodeURIComponent(retour)}` : ''}&session_id={CHECKOUT_SESSION_ID}`;

  return (
    <AuthCard className="dos-carte dos-carte--large" title="Mes abonnements" subtitle="Formules, dossiers supplémentaires et packs de toute la suite SEmply">
      {espaces.length > 1 ? (
        <div className="field">
          <label htmlFor="abo-espace">Espace</label>
          <select id="abo-espace" value={espaceId ?? ''} onChange={(e) => choisirEspace(e.target.value)}>
            {espaces.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {compte ? (
        <BlocFacturation
          key={compte.spaceId}
          spaceId={compte.spaceId}
          profil={compte.facturation.profil}
          modifiable={compte.gestionnaire}
          onEnregistre={() => void chargerCompte()}
          autreSociete={compte.gestionnaire}
        />
      ) : null}

      <nav className="dos-choix" aria-label="Produits">
        {lances.map((p) => (
          <button
            key={p}
            type="button"
            className={`dos-pastille${p === produit ? ' is-actif' : ''}`}
            aria-pressed={p === produit}
            onClick={() => choisir(p)}
          >
            {produits.find((x) => x.produit === p)?.nom ?? NOMS_PRODUITS[p]}
          </button>
        ))}
      </nav>

      <Abonnement
        key={`${espaceId ?? ''}-${produit}`}
        spaceId={espaceId}
        apiUrl={`${API_URL}/espace`}
        getToken={SESSION_PORTAIL}
        product={produit}
        nomProduit={nomProduit}
        description={presentation.description}
        unite={presentation.unite}
        avecQuantite={presentation.avecQuantite ?? true}
        returnUrl={retourStripe}
        renderCheckout={(secret: string) => <PaiementStripe clientSecret={secret} />}
      />

      {compte?.gestionnaire ? <FacturesEspace spaceId={compte.spaceId} /> : null}

      <p className="dos-actions">
        {retour ? (
          <a className="btn btn-secondaire" href={retour}>
            Revenir à {nomProduit}
          </a>
        ) : null}
        <Link to="/produits">Mes outils SEmply</Link>
      </p>
    </AuthCard>
  );
}
